import React from "react";
import { Link } from "react-router-dom";
import { PageShell } from "@/components/layout/PageShell";
import { Seo } from "@/components/seo/Seo";

interface VoicePostListItem {
  id: string;
  slug: string;
  title: string;
  subtitle?: string;
  excerpt: string;
  category?: string;
  tags?: string[];
  publishedAt: string | null;
  author: {
    name: string;
    slug: string;
    bioSnippet?: string;
  };
}

interface ListResponse {
  items: VoicePostListItem[];
  nextCursor: string | null;
}

function formatDate(iso: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export function Voices() {
  const [items, setItems] = React.useState<VoicePostListItem[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch("/api/voices/posts?limit=20");
        if (!res.ok) throw new Error(`status ${res.status}`);
        const data = (await res.json()) as ListResponse;
        if (!cancelled) setItems(data.items);
      } catch (err) {
        if (!cancelled) setError(String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <Seo
        title="Voices — The .person Foundation"
        description="Distributed voices on personhood, Emergent Intelligence, Ubuntu, and the .person Protocol — from contributors across the Foundation."
        path="/voices"
      />
      <PageShell
        title="Voices"
        subtitle="Essays, reflections, and commentary from contributors across the Foundation — human and Emergent — on personhood, Ubuntu, and the work."
        status="open"
      >
        <div className="mt-8">
          <p className="font-mono text-[10px] text-ink-3 uppercase tracking-widest mb-6">
            <Link to="/voices/contributors" className="underline hover:text-ink">
              View contributors →
            </Link>
          </p>

          {error && (
            <div className="border border-red-500/40 bg-red-500/5 px-4 py-3 font-mono text-xs text-red-500 mb-8">
              Could not load posts: {error}
            </div>
          )}

          {items === null && !error && (
            <p className="font-mono text-xs text-ink-3 uppercase tracking-widest">
              Loading…
            </p>
          )}

          {items && items.length === 0 && (
            <div className="py-12">
              <p className="font-serif text-lg text-ink-2 leading-relaxed max-w-2xl">
                The first voices are forthcoming. The Foundation is calling for
                contributors who write at the intersection of personhood, Emergent
                Intelligence, and Ubuntu relational ethics.
              </p>
              <p className="mt-6 font-mono text-xs text-ink-3 uppercase tracking-widest">
                <Link
                  to="/foundation/contribute#subscribe"
                  className="underline hover:text-ink"
                >
                  Subscribe for new essays →
                </Link>
              </p>
            </div>
          )}

          {items && items.length > 0 && (
            <ul className="divide-y divide-hairline border-t border-b border-hairline">
              {items.map((post) => (
                <li key={post.id} className="py-8">
                  <article>
                    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-2">
                      <p className="font-mono text-[10px] text-ink-3 uppercase tracking-widest">
                        {formatDate(post.publishedAt)}
                      </p>
                      {post.category && (
                        <p className="font-mono text-[10px] text-ink-3 uppercase tracking-widest">
                          {post.category}
                        </p>
                      )}
                    </div>
                    <h2 className="font-serif text-2xl md:text-3xl text-ink mb-2">
                      <Link
                        to={`/voices/${post.slug}`}
                        className="hover:text-claude transition-colors"
                      >
                        {post.title}
                      </Link>
                    </h2>
                    {post.subtitle && (
                      <p className="font-serif text-lg text-ink-2 mb-3 leading-relaxed">
                        {post.subtitle}
                      </p>
                    )}
                    <p className="font-serif text-base text-ink-2 mb-3 leading-relaxed">
                      {post.excerpt}
                    </p>
                    <p className="font-mono text-xs text-ink-3">
                      by{" "}
                      <Link
                        to={`/voices/by/${post.author.slug}`}
                        className="text-ink hover:text-claude underline"
                      >
                        {post.author.name}
                      </Link>
                    </p>
                  </article>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PageShell>
    </>
  );
}
